import React, { useState } from "react";

import cx from "classnames";

import { setStateValue } from "@kazoohr/helpers";

import { useOutsideClick } from "../../helpers/useOutsideClick";

import type { User, MenuItem } from "./types";
import styles from "./UserMenu.scss";
import { UserMenuButton } from "./UserMenuButton";
import { UserNav } from "./UserNav";

export interface UserMenuProps {
  /** Optional class name for custom styling */
  className?: string;
  /** Whether or not signout button exists */
  hasSignOut?: boolean;
  /** Results from successful query to `dashboard_userMenu` */
  menuItems: MenuItem[];
  /** Called when sign out button clicked */
  onSignOut?: () => void;
  /** Optional custom link renderer */
  renderLink?: (props: MenuItem) => React.ReactNode;
  /** Text displayed on sign out button */
  signOutText?: string;
  /** Kazoo user object, used for displaying profile image, name and job title */
  user?: User | null;
  /** Disables button and removes caret */
  isPreview?: boolean;
}

/**
 * An accessible UserMenu component that displays a user's profile image, name, job title, and a dropdown menu of links.
 */
export function UserMenu({
  className = "",
  hasSignOut,
  menuItems,
  onSignOut,
  renderLink,
  signOutText,
  user,
  isPreview = false,
}: UserMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = React.useRef<HTMLDivElement>(null);

  useOutsideClick({
    elementRefs: [wrapperRef],
    handler: setStateValue(setIsOpen, false),
    dependencies: [isOpen],
    ignoreEffect: !isOpen,
  });

  return (
    <div ref={wrapperRef} className={styles["user-menu-wrapper"]}>
      <div
        role="presentation"
        className={cx(styles["user-menu"], className)}
        data-testid="user-menu"
      >
        <UserMenuButton
          testid={
            isOpen ? "user-menu-button-expanded" : "user-menu-button-closed"
          }
          isOpen={isOpen}
          isPreview={isPreview}
          user={user}
          setOpen={setIsOpen}
        />
        <UserNav
          isOpen={isOpen}
          setIsOpen={setIsOpen}
          menuItems={menuItems}
          renderLink={renderLink}
          hasSignOut={hasSignOut}
          onSignOut={onSignOut}
          signOutText={signOutText}
        />
      </div>
    </div>
  );
}
